import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import Button from "@/components/base/Button";
import { buildPayroll } from "@/utils/payroll";
import { exportPayrollPdf } from "@/utils/payrollPdf";
import { getSchoolInfo } from "@/services/schoolService";
import type { Cycle, SchoolInfo, Teacher } from "@/types";
import PayrollSheet from "./PayrollSheet";

interface Props {
  open: boolean;
  onClose: () => void;
  teachers: Teacher[];
  cycles: Cycle[];
}

const EMPTY_SCHOOL: SchoolInfo = {
  name: "",
  address: "",
  city: "",
  phone: "",
  email: "",
  slogan: "",
  schoolYear: "",
  logo: "",
  signatoryName: "",
  signatoryTitle: "",
  stateName: "",
  stateMotto: "",
  ministryName: "",
  currencySymbol: "FG",
  currencyName: "franc guinéen",
  currencyNamePlural: "francs guinéens",
  plan: "ETABLISSEMENT",
  subscriptionEndsAt: null,
};

export default function PayrollPrintModal({ open, onClose, teachers, cycles: allCycles }: Props) {
  const [scope, setScope] = useState("all");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) setScope("all");
  }, [open]);

  const summary = useMemo(() => buildPayroll(teachers, allCycles), [teachers, allCycles]);

  /* En-tête de la fiche : la vraie fiche de l’école. */
  const [schoolInfo, setSchoolInfo] = useState<SchoolInfo>(EMPTY_SCHOOL);
  useEffect(() => {
    if (open) getSchoolInfo().then(setSchoolInfo).catch(() => undefined);
  }, [open]);

  if (!open) return null;

  const cycles = scope === "all" ? summary.cycles : summary.cycles.filter((c) => c.id === scope);
  const scopeLabel =
    scope === "all"
      ? "Vue globale (Primaire + Secondaire)"
      : `Cycle ${summary.cycles.find((c) => c.id === scope)?.label ?? ""}`;

  const handlePdf = () =>
    exportPayrollPdf(cycles, {
      schoolName: schoolInfo.name,
      schoolYear: schoolInfo.schoolYear,
      scopeLabel,
    });

  return createPortal(
    <div className="print-portal fixed inset-0 z-[110] overflow-y-auto bg-foreground-950/45 backdrop-blur-[2px]">
      <div className="no-print sticky top-0 z-10 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-md bg-primary-50 text-primary-700 flex items-center justify-center">
              <i className="ri-file-list-3-line text-lg"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold font-heading text-foreground-950">
                Fiche de la masse salariale
              </h3>
              <p className="text-xs text-foreground-500">Aperçu prêt à imprimer — Session {schoolInfo.schoolYear}</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm cursor-pointer focus:outline-none"
            >
              <option value="all">Vue globale</option>
              {summary.cycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
            <Button variant="outline" icon="ri-file-pdf-2-line" onClick={handlePdf}>
              Exporter en PDF
            </Button>
            <Button icon="ri-printer-line" onClick={() => window.print()}>
              Imprimer
            </Button>
            <button
              onClick={onClose}
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 hover:text-foreground-800 transition-colors cursor-pointer"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="print-pad px-4 py-6 md:px-8 md:py-10">
        <div className="mx-auto w-full max-w-[900px]">
          <PayrollSheet cycles={cycles} scopeLabel={scopeLabel} school={schoolInfo} />
        </div>
      </div>
    </div>,
    document.body,
  );
}